import React, { useState } from 'react';
import Icon from './AppIcon';

const TerminalWindow = ({ 
  isMinimized = false,
  onMinimize,
  onMaximize,
  onClose,
  children,
  title = "terminal",
  className = "",
  width = "w-full max-w-4xl mx-auto",
  height = "min-h-[460px]",
  showMetadata = true,
  metadata = {
    shell: "zsh",
    dimensions: "80x24",
    encoding: "UTF-8"
  }
}) => {
  const [isMaximized, setIsMaximized] = useState(false);

  const handleMaximize = () => {
    const newMaximizedState = !isMaximized;
    setIsMaximized(newMaximizedState);
    onMaximize?.(newMaximizedState);
  };

  const handleMinimize = () => {
    onMinimize?.();
  };

  const handleClose = () => {
    onClose?.();
  };

  return (
    <div className={`
      bg-card border border-terminal-muted rounded-lg overflow-hidden terminal-glow
      ${isMaximized ? 'fixed inset-4 z-50' : width}
      ${isMinimized ? 'h-10' : height}
      ${className}
    `}>
      {/* Terminal Header */}
      <div className="bg-muted/30 border-b border-terminal-muted px-4 py-2 flex items-center justify-between">
        <div className="flex items-center space-x-3">
          {/* macOS-style Control Buttons */}
          <div className="flex items-center space-x-1 group">
            {/* Close Button */}
            <div 
              className="w-3 h-3 bg-destructive rounded-full cursor-pointer hover:bg-destructive/80 flex items-center justify-center relative" 
              onClick={handleClose}
            >
              <svg 
                className="w-2 h-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 absolute" 
                fill="none" 
                stroke="#1a1a1a" 
                strokeWidth="2" 
                viewBox="0 0 8 8"
              >
                <path d="M1 1L7 7M7 1L1 7" />
              </svg>
            </div>
            
            {/* Minimize Button */}
            <div 
              className="w-3 h-3 bg-warning rounded-full cursor-pointer hover:bg-warning/80 flex items-center justify-center relative" 
              onClick={handleMinimize}
            >
              <svg 
                className="w-2 h-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 absolute" 
                fill="none" 
                stroke="#1a1a1a" 
                strokeWidth="2" 
                viewBox="0 0 8 8"
              >
                <path d="M1 4H7" />
              </svg>
            </div>
            
            {/* Maximize Button */}
            <div 
              className="w-3 h-3 bg-success rounded-full cursor-pointer hover:bg-success/80 flex items-center justify-center relative" 
              onClick={handleMaximize}
            >
              <svg 
                className="w-2 h-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 absolute" 
                fill="none" 
                stroke="#1a1a1a" 
                strokeWidth="2" 
                viewBox="0 0 8 8"
              >
                <path d="M2 2L6 6M6 2L2 6" strokeLinecap="square" />
                <path d="M2 2H6M6 2V6M6 6H2M2 6V2" strokeWidth="1" />
              </svg>
            </div>
          </div>
          
          {/* Terminal Title */}
          <div className="flex items-center space-x-2">
            <Icon name="Terminal" size={16} className="text-primary" />
            <span className="font-fira text-sm text-foreground">{title}</span>
          </div>
        </div>
        
        {/* Terminal Metadata */}
        {showMetadata && (
          <div className="flex items-center space-x-2 text-xs text-muted-foreground font-source">
            {metadata.shell && <span>{metadata.shell}</span>}
            {metadata.shell && metadata.dimensions && <span>•</span>}
            {metadata.dimensions && <span>{metadata.dimensions}</span>}
            {metadata.dimensions && metadata.encoding && <span>•</span>}
            {metadata.encoding && <span>{metadata.encoding}</span>}
          </div>
        )}
      </div>

      {/* Terminal Content */}
      {!isMinimized && (
        <div className={`relative bg-background p-4 h-full ${height} font-fira text-sm overflow-hidden`}>
          {children}
        </div>
      )}
    </div>
  );
};

export default TerminalWindow;
